import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { useNetworkContext } from "../../../network";
import { useControlContext } from "../../../control";
import { getMessagesByReply } from "../../actions";
import { selectChatId } from "../../selectors";
import { Context } from "./context";
import type { ProviderProps } from "./types";
import styles from "./animation.module.scss";

const EMPTY: [string, string] = ["", ""];

const getElement = (id: string) => document.getElementById(id);
const setAnimation = (elem: HTMLElement) => {
  elem.classList.add(styles.flashing);

  setTimeout(() => {
    elem.classList.remove(styles.flashing);
  }, 2000);
};

export const ReplyPathProvider = ({ children }: ProviderProps) => {
  const dispatch = useAppDispatch();
  const { api } = useControlContext();
  const { isGetMessagesByReplySuccess } = useNetworkContext();

  const chatId = useAppSelector(selectChatId);

  const [[childReplyMessageId /* parentReplyMessageId */], setReplyMessage] =
    useState<[string, string]>(EMPTY);

  const { getMessagesApi } = api;

  const handleClickOnReplyMessage = (childId: string, parentId: string) => {
    setReplyMessage([childId, parentId]);
  };

  const handleResetReplyMessage = () => {
    setReplyMessage(EMPTY);
  };

  useEffect(() => {
    if (!childReplyMessageId) {
      return;
    }

    const messageDiv = getElement(childReplyMessageId);

    if (messageDiv) {
      messageDiv.scrollIntoView({ behavior: "smooth", block: "start" });

      setAnimation(messageDiv);
      handleResetReplyMessage();

      return;
    }

    const params = { getMessagesApi, chatId, messageId: childReplyMessageId };

    dispatch(getMessagesByReply(params));
  }, [childReplyMessageId]);

  useEffect(() => {
    if (!isGetMessagesByReplySuccess) {
      return;
    }

    const messageDiv = getElement(childReplyMessageId);

    if (messageDiv) {
      messageDiv.scrollIntoView({ behavior: "smooth", block: "center" });

      setAnimation(messageDiv);
      handleResetReplyMessage();
    }
  }, [isGetMessagesByReplySuccess]);

  return (
    <Context.Provider
      value={{ handleClickOnReplyMessage, handleResetReplyMessage }}
    >
      {children}
    </Context.Provider>
  );
};
